import { ExportOutlined } from "@ant-design/icons";
import {
  OnePasswordTokenVault,
  SIGN_IN_ADDRESS_PATTERN,
} from "@p0security/integrations-one-password-shared/components";
import {
  VAULT_OUTSIDE_CONNECTOR_CLOUD,
  renderHosting,
} from "@p0security/integrations-one-password-shared/hosting";
import type {
  OnePasswordAccountConfig,
  OnePasswordConnectorHosting,
} from "@p0security/integrations-one-password-shared/types";
import { shellLines } from "@p0security/integrations-vaulted-credential-shared";
import {
  Alert,
  Button,
  CommandDisplay,
  Flex,
  Steps,
  Typography,
} from "@p0security/ui";
import { assertNever } from "@p0security/util/assertNever";
import type { ComponentProps, ReactNode } from "react";

import type { ReadGrantBlock } from "./account-iac.ts";
import { PLACEHOLDER_DOMAIN, toEmailDomain } from "./connector-iac.ts";

const { Paragraph, Text } = Typography;

type SecretManager = OnePasswordAccountConfig["secretManager"];

/** What an account's page can show of its read grant. */
export type ReadGrant =
  | {
      status: "ready";
      /** The Terraform that lets the connector read the token, in the order
       * to apply it */
      blocks: ReadGrantBlock[];
      hosting: OnePasswordConnectorHosting;
      /** The AWS account that holds the secret, when it isn't the
       * connector's */
      secretAccountId: string | undefined;
    }
  /** The account's connector isn't installed */
  | { status: "no-connector" }
  /** The secret isn't in the connector's cloud, so no Terraform grants it */
  | { status: "secret-outside-cloud"; hosting: OnePasswordConnectorHosting };

/** One of the steps that install an account. */
type Step = { content: ReactNode; status?: "error" | "finish"; title: string };

/** The customer-managed KMS key for a secret in another account than the
 * connector's, in the command that creates the secret. */
const KMS_KEY_ARN = "<kms-key-arn>";

// Reads the token without echoing it or leaving it in the shell's history.
// Each command below takes it from standard input, so it's never an argument,
// which other users of the machine could list. Not OP_SERVICE_ACCOUNT_TOKEN,
// so it doesn't sign the shell's own op CLI in as the service account.
const READ_TOKEN = "read -rs ONEPASSWORD_TOKEN";

const CLEAR_TOKEN = "unset ONEPASSWORD_TOKEN";

const pipeToken = (...lines: string[]) =>
  `printf %s "$ONEPASSWORD_TOKEN" | ${shellLines(...lines)}`;

/** Stands for each vault's ID in the command that creates the service
 * account. */
const VAULT_ID_PLACEHOLDER = "<vault-id>";

/** The op CLI command that creates the account's service account, with Read
 * Items and Share Items on one vault: the customer repeats `--vault` for each
 * vault people may request items from. The flags are as 1Password's
 * `op service-account create` reference gives them: `share_items` needs
 * `read_items`. */
const CREATE_SERVICE_ACCOUNT = shellLines(
  'op service-account create "P0 connector"',
  `--vault ${VAULT_ID_PLACEHOLDER}:read_items,share_items`
);

/** The secret manager a token is in, as its option is labelled. */
const toSecretManagerLabel = (secretManager: SecretManager) =>
  OnePasswordTokenVault.options[secretManager.type].label;

/** The account's sign-in page, when its id is an address P0 can link to: a
 * sign-in address, such as acme.1password.com, and nothing else. */
const toSignInUrl = (accountId: string) => {
  const address = accountId.trim();
  return SIGN_IN_ADDRESS_PATTERN.test(address)
    ? `https://${address}`
    : undefined;
};

/** How to store the token as the account's secret: where the secret is, such
 * as "account 123456789012 and region us-west-2", the command that creates
 * it, and the command for a secret that already exists. */
const toStoreInstructions = (
  { secretManager, tokenSecretName: name }: OnePasswordAccountConfig,
  { crossAccount }: { crossAccount: boolean }
): { create: string; location: string; update: string } => {
  switch (secretManager.type) {
    case "aws-sm":
      return {
        create: pipeToken(
          "aws secretsmanager create-secret",
          `--name ${name}`,
          ...(crossAccount ? [`--kms-key-id ${KMS_KEY_ARN}`] : []),
          "--secret-string file:///dev/stdin",
          `--region ${secretManager.secretsRegion}`
        ),
        location: `account ${secretManager.install} and region ${secretManager.secretsRegion}`,
        update: pipeToken(
          "aws secretsmanager put-secret-value",
          `--secret-id ${name}`,
          "--secret-string file:///dev/stdin",
          `--region ${secretManager.secretsRegion}`
        ),
      };
    case "gcp-sm":
      return {
        create: pipeToken(
          `gcloud secrets create ${name}`,
          `--project ${secretManager.install}`,
          "--data-file=-"
        ),
        location: `project ${secretManager.install}`,
        update: pipeToken(
          `gcloud secrets versions add ${name}`,
          `--project ${secretManager.install}`,
          "--data-file=-"
        ),
      };
    default:
      throw assertNever(secretManager);
  }
};

/** Whether the connector's allowed-domain `pattern` lets it act for
 * `domain`, as the connector tests it: unanchored, against the part of an
 * email after the "@". A pattern that doesn't compile allows nothing. */
export const allowsDomain = ({
  domain,
  pattern,
}: {
  domain: string;
  pattern: string;
}) => {
  try {
    return new RegExp(pattern.trim()).test(domain);
  } catch {
    return false;
  }
};

/** Says how to create the account's service account, in 1Password or with
 * its CLI. */
const CreateServiceAccount: React.FC<{
  /** The account's sign-in address */
  accountId: string;
}> = ({ accountId }) => {
  const signInUrl = toSignInUrl(accountId);
  return (
    <Flex gap={8} vertical>
      <Text>
        Service accounts need a 1Password Business or Enterprise plan. Sign in
        to {accountId.trim()} as an owner or administrator, go to Developer,
        then Directory, and create a service account under Service Accounts.
        Give it access to exactly the vaults people may request items from, with{" "}
        <Text strong>Read Items</Text> and <Text strong>Share Items</Text> on
        each, and no other permission. P0 lists the vaults it can see, so people
        can request items only from those. 1Password shows the token once, so
        copy it. It starts with <Text code>ops_</Text>.
      </Text>
      <Text>
        Service accounts can&apos;t access Personal, Private or Employee vaults,
        so P0 can&apos;t share items from those.
      </Text>
      {signInUrl && (
        <div>
          <Button
            href={signInUrl}
            icon={<ExportOutlined />}
            rel="noopener noreferrer"
            target="_blank"
          >
            Open 1Password
          </Button>
        </div>
      )}
      <Text>
        Or create it with the 1Password CLI, signed in as an owner or
        administrator. Replace <Text code>{VAULT_ID_PLACEHOLDER}</Text> with a
        vault&apos;s ID, and add one <Text code>--vault</Text> for each vault:
      </Text>
      <CommandDisplay commands={CREATE_SERVICE_ACCOUNT} />
    </Flex>
  );
};

/** Says how to store the token as the account's secret, with the commands
 * that do. */
const StoreToken: React.FC<{
  crossAccount: boolean;
  item: OnePasswordAccountConfig;
}> = ({ crossAccount, item }) => {
  const { secretManager, tokenSecretName: name } = item;
  const { create, location, update } = toStoreInstructions(item, {
    crossAccount,
  });
  return (
    <Flex gap={8} vertical>
      <Text>
        Store the token in {toSecretManagerLabel(secretManager)} as the secret{" "}
        <Text code>{name}</Text>, in {location}.{" "}
        {crossAccount && (
          <>
            The connector runs in another AWS account, so the secret needs a
            customer-managed KMS key: replace <Text code>{KMS_KEY_ARN}</Text>{" "}
            with that key&apos;s ARN.{" "}
          </>
        )}
        Run this first, paste the token and press Enter. It reads the token
        without showing it:
      </Text>
      <CommandDisplay commands={READ_TOKEN} />
      <Text>Then create the secret:</Text>
      <CommandDisplay commands={create} />
      <Text>
        If the secret already exists, store the token as a new version instead:
      </Text>
      <CommandDisplay commands={update} />
      <Text>Then clear the token from your shell:</Text>
      <CommandDisplay commands={CLEAR_TOKEN} />
      <Text>
        P0 never reads the token. Only your connector does. To rotate it later,
        store the new token the same way, as a new version of this secret.
      </Text>
    </Flex>
  );
};

/** The step that says the account's connector is deployed. */
const toDeployedStep = (
  connectorId: string,
  hosting: OnePasswordConnectorHosting
): Omit<Step, "title"> => ({
  content: (
    <Text>
      Done: the connector {connectorId} runs as {renderHosting(hosting)}. It
      shares items only with the email domains its deployment allows.
    </Text>
  ),
  status: "finish",
});

/** Says where to apply the Terraform that lets the connector read the
 * token. */
const GrantRead: React.FC<{
  /** The AWS account that holds the secret, when it isn't the connector's */
  secretAccountId: string | undefined;
}> = ({ secretAccountId }) => (
  <Text>
    Apply the Terraform below. It lets the connector read this one secret, and
    no other.
    {secretAccountId && (
      <>
        {" "}
        The secret and the connector are in different AWS accounts, so there are
        two configurations: one for the connector&apos;s account, and one for
        account {secretAccountId}, which holds the secret.
      </>
    )}
  </Text>
);

/** What the steps that depend on the account's connector show, for each state
 * of its read grant: the connector's own step, what the step that grants the
 * read says, and whether the secret is in another AWS account than the
 * connector. */
const toConnectorSteps = (
  connectorId: string,
  grant: ReadGrant
): { crossAccount: boolean; deploy: Omit<Step, "title">; read: ReactNode } => {
  switch (grant.status) {
    case "no-connector":
      return {
        crossAccount: false,
        deploy: {
          content: (
            <Alert
              message={`The connector ${connectorId} isn't installed. Install it again under Connector, with the same ID and hosting, or remove this account and add it again with another connector.`}
              showIcon
              type="error"
            />
          ),
          status: "error",
        },
        read: <Text>Install the connector first.</Text>,
      };
    case "ready":
      return {
        crossAccount: grant.secretAccountId !== undefined,
        deploy: toDeployedStep(connectorId, grant.hosting),
        read: <GrantRead secretAccountId={grant.secretAccountId} />,
      };
    case "secret-outside-cloud":
      return {
        crossAccount: false,
        deploy: toDeployedStep(connectorId, grant.hosting),
        read: (
          <Alert
            message={`${VAULT_OUTSIDE_CONNECTOR_CLOUD} Remove this account and add it again.`}
            showIcon
            type="error"
          />
        ),
      };
    default:
      throw assertNever(grant);
  }
};

/** Says what "Next" checks, and that "Finish" on the next page completes the
 * install. Once the connector has reported its allowed domains, it warns when
 * they leave out the admin's own. */
const Validate: React.FC<{
  /** The account's sign-in address */
  accountId: string;
  adminEmail: string | null | undefined;
  allowedDomains: string | undefined;
}> = ({ accountId, adminEmail, allowedDomains }) => {
  const domain = toEmailDomain(adminEmail);
  return (
    <Flex gap={8} vertical>
      <Text>
        Click &quot;Next&quot;. P0 asks the connector to read the token, sign in
        to 1Password with it, list the vaults the service account can see, which
        must be at least one, and check that your account&apos;s sharing policy
        allows sharing an item with a specific person by email. It shares
        nothing. If none of those vaults has an item, the sharing check is
        skipped. If a check fails, P0 says which one. If the connector is denied
        the secret right after you apply the Terraform, wait a few seconds and
        click &quot;Next&quot; again.
      </Text>
      <Text>
        When the checks pass, click &quot;Finish&quot; on the next page. Items
        in {accountId} can&apos;t be requested until you do.
      </Text>
      {allowedDomains &&
        domain &&
        !allowsDomain({ domain, pattern: allowedDomains }) && (
          <Alert
            message={`The connector allows the email domains ${allowedDomains}, which leaves out your own, ${domain}. If people at ${domain} should be able to request items in ${accountId}, change domain_allow_pattern in the connector's Terraform and apply it.`}
            showIcon
            type="warning"
          />
        )}
    </Flex>
  );
};

/**
 * The steps that install one 1Password account, in the order the customer
 * takes them, shown above the account's Terraform.
 *
 * The connector is deployed before the account is added, since the
 * account's form lists only installed connectors, so that step shows as
 * done, or as failed if the connector has since been removed.
 */
export const AccountSteps: React.FC<{
  /** The 1Password account's sign-in address, which is its item's id */
  accountId: string;
  /** The signed-in admin's email, whose domain the allowed domains are
   * checked against */
  adminEmail: string | null | undefined;
  grant: ReadGrant;
  item: OnePasswordAccountConfig;
}> = ({ accountId, adminEmail, grant, item }) => {
  const { crossAccount, deploy, read } = toConnectorSteps(
    item.connector,
    grant
  );
  const steps: Step[] = [
    {
      content: <CreateServiceAccount accountId={accountId} />,
      title: "Create the service account",
    },
    {
      content: <StoreToken crossAccount={crossAccount} item={item} />,
      title: `Store the token in ${toSecretManagerLabel(item.secretManager)}`,
    },
    { ...deploy, title: "Deploy the connector" },
    { content: read, title: "Let the connector read the token" },
    {
      content: (
        <Validate
          accountId={accountId}
          adminEmail={adminEmail}
          allowedDomains={item.allowedDomains}
        />
      ),
      title: "Validate",
    },
  ];
  const items: ComponentProps<typeof Steps>["items"] = steps.map(
    ({ content, status, title }) => ({
      description: content,
      status: status ?? "process",
      title,
    })
  );
  return <Steps direction="vertical" items={items} />;
};

/** What to do with a connector's Terraform, shown above it. */
export const ConnectorHelp: React.FC<{
  /** The signed-in admin's email, whose domain the Terraform's allowed-domain
   * pattern starts as */
  adminEmail: string | null | undefined;
  connectorId: string;
  hosting: OnePasswordConnectorHosting;
}> = ({ adminEmail, connectorId, hosting }) => (
  <>
    <Paragraph>
      The Terraform below deploys the connector {connectorId} as{" "}
      {renderHosting(hosting)}, and lets P0 invoke it. The connector reaches
      1Password over the internet, so it needs no VPC. If your 1Password account
      has a firewall, allow the connector&apos;s outbound traffic.
    </Paragraph>
    <Paragraph>
      Before you apply it, set <Text code>domain_allow_pattern</Text> to the
      email domains the connector may share items with.{" "}
      {toEmailDomain(adminEmail)
        ? "P0 fills in your own email's domain as a start."
        : `P0 doesn't know your email's domain, so it fills in ${PLACEHOLDER_DOMAIN}, which matches nobody: replace it.`}{" "}
      The connector doesn&apos;t anchor the pattern, so keep the{" "}
      <Text code>^</Text> and <Text code>$</Text>, as in{" "}
      <Text code>^acme\.com$</Text>: without them, <Text code>acme\.com</Text>{" "}
      also matches acme.com.evil.io. The pattern lives only in your deployment:
      P0 can&apos;t change it, and shows the one deployed on each account as
      &quot;Allowed domains&quot;.
    </Paragraph>
    <Paragraph>
      Then click &quot;Next&quot;, and P0 checks that it can invoke the
      connector. When it can, click &quot;Finish&quot; on the next page:
      accounts can&apos;t name the connector until you do. Each account&apos;s
      own Terraform then lets the connector read that account&apos;s service
      account token.
    </Paragraph>
  </>
);
